import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Store, CheckCircle2, ExternalLink, Package } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminShopAlerts, markAdminShopAlert } from "@/lib/api";

const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");
const TRIGGER = { shop_created: "Shop created", first_product: "Shop live with first product", listed: "Shop made public", vendor_approved: "Vendor approved" };

function AlertRow({ a, onSeen, busy }) {
  return (
    <div className={`rounded-xl border p-3 min-w-0 ${a.seen ? "bg-white border-slate-100" : "bg-amber-50/60 border-amber-200"}`} data-testid={`shop-alert-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 break-words flex items-center gap-1.5"><Store className="w-4 h-4 text-emerald-700 flex-shrink-0" /> {a.shop_name || a.shop_id}</div>
          <div className="text-[11px] text-slate-500 break-all">Vendor: {a.vendor_name || "—"}{a.vendor_email ? ` · ${a.vendor_email}` : ""} · ID {a.vendor_id}</div>
        </div>
        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${a.seen ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-800"}`} data-testid={`shop-alert-status-${a.id}`}>{a.seen ? "Seen" : "New"}</span>
      </div>
      <div className="mt-1.5 text-xs text-slate-600" data-testid={`shop-alert-time-${a.id}`}>{TRIGGER[a.trigger] || a.trigger} · {fmtIST(a.event_at)}{a.pincode ? ` · PIN ${a.pincode}` : ""}</div>
      {a.first_product && <div className="mt-1 text-xs text-slate-600 flex items-center gap-1 break-words" data-testid={`shop-alert-first-product-${a.id}`}><Package className="w-3.5 h-3.5 flex-shrink-0" /> First product: {a.first_product.name} · {fmtIST(a.first_product.at)}</div>}
      {a.seen && <div className="mt-1 text-[11px] text-slate-400">Seen {fmtIST(a.seen_at)}{a.seen_by ? ` by ${a.seen_by}` : ""}</div>}
      <div className="mt-2 flex flex-wrap gap-2">
        <Link to={a.shop_slug ? `/v/${a.shop_slug}` : `/shop/${a.shop_id}`} target="_blank" className="inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-emerald-500 px-3 py-1.5 text-xs font-semibold min-h-[32px]" data-testid={`shop-alert-open-${a.id}`}><ExternalLink className="w-3.5 h-3.5" /> Open shop</Link>
        {!a.seen && <button onClick={() => onSeen(a)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold min-h-[32px]" data-testid={`shop-alert-seen-${a.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Dekh liya</button>}
      </div>
    </div>
  );
}

// Admin-only list of shops that went public for the first time (recorded from feature start; no backfill).
export default function AdminShopAlerts({ onUnseenChange }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = () => fetchAdminShopAlerts().then((r) => { setD(r); onUnseenChange?.(r.unseen); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onSeen = async (a) => {
    setBusy(a.id);
    try { await markAdminShopAlert(a.id); await load(); } catch { toast.error("Could not mark as seen"); } finally { setBusy(null); }
  };
  if (err) return <div className="text-sm text-rose-700" data-testid="shop-alerts-error">Data unavailable — could not load shop alerts.</div>;
  if (!d) return <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading shop alerts…</div>;
  const unseen = d.items.filter((a) => !a.seen), seen = d.items.filter((a) => a.seen);
  return (
    <div className="space-y-4" data-testid="admin-shop-alerts">
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600" data-testid="shop-alerts-note">
        One alert per shop, when it first becomes public. Recorded from {fmtIST(d.since)} onwards; older shops are not included. Unlisted, pending and test shops are skipped. "Dekh liya" only changes the alert status.
      </div>
      <div>
        <h3 className="text-sm font-bold mb-2" data-testid="shop-alerts-unseen-count">New ({unseen.length})</h3>
        {unseen.length ? <div className="space-y-2">{unseen.map((a) => <AlertRow key={a.id} a={a} onSeen={onSeen} busy={busy === a.id} />)}</div>
          : <div className="text-sm text-slate-500" data-testid="shop-alerts-empty">No new shop alerts.</div>}
      </div>
      {seen.length > 0 && (
        <div>
          <h3 className="text-sm font-bold mb-2 text-slate-500" data-testid="shop-alerts-seen-count">Seen ({seen.length})</h3>
          <div className="space-y-2">{seen.map((a) => <AlertRow key={a.id} a={a} onSeen={onSeen} busy={false} />)}</div>
        </div>
      )}
    </div>
  );
}
